import React, { useState, useEffect } from "react";
import { db } from "../../firebase.config";
import {
  collection,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  getDocs,
  arrayUnion,
  arrayRemove,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import "./CodeLibrary.scss";
import Sidebar from "../Sidebar/Sidebar";
import DetailsPanel from "../DetailsPanel/DetailsPanel";
import PopupForm from "../PopupForm/PopupForm";

const FUNC_FIELDS = [
  { label: "Name", name: "name", type: "text" },
  { label: "Function", name: "func", type: "textarea" },
  { label: "Description", name: "desc", type: "textarea" },
];

function CodeLibrary() {
  const [funcs, setFuncs] = useState([]);
  const [popupActive, setPopupActive] = useState(false);
  const [form, setForm] = useState({ name: "", func: "", desc: "" });
  const [favourites, setFavourites] = useState([]);
  const [user, setUser] = useState(null);
  const [showOnlyFavourites, setShowOnlyFavourites] = useState(false);
  const [selectedFunc, setSelectedFunc] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const funcsCollectionRef = collection(db, "functions");

  useEffect(() => {
    const unsubscribe = onSnapshot(funcsCollectionRef, (snapshot) => {
      setFuncs(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setUser(firebaseUser || null);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    const fetchFavourites = async () => {
      const userQuery = query(collection(db, "users"), where("id", "==", user.uid));
      const querySnapshot = await getDocs(userQuery);
      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        setFavourites(userData.favourites || []);
      }
    };
    fetchFavourites();
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.func || !form.desc) return alert("Please fill out all fields");
    await addDoc(funcsCollectionRef, form);
    setForm({ name: "", func: "", desc: "" });
    setPopupActive(false);
  };

  const removeFunc = async (id) => {
    if (window.confirm("Are you sure you want to delete?")) {
      await deleteDoc(doc(db, "functions", id));
      if (selectedFunc?.id === id) setSelectedFunc(null);
    }
  };

  const handleCopyCode = ({ func }) => navigator.clipboard.writeText(func);

  const toggleFavourite = async (func) => {
    const userQuery = query(collection(db, "users"), where("id", "==", user.uid));
    const querySnapshot = await getDocs(userQuery);
    if (querySnapshot.empty) return;

    const userDoc = querySnapshot.docs[0];
    const userRef = doc(db, "users", userDoc.id);
    const isFav = favourites.some((fav) => fav.id === func.id);

    await updateDoc(userRef, {
      favourites: isFav
        ? arrayRemove({ id: func.id, name: func.name })
        : arrayUnion({ id: func.id, name: func.name }),
    });

    setFavourites((prev) =>
      isFav ? prev.filter((f) => f.id !== func.id) : [...prev, { id: func.id, name: func.name }]
    );
  };

  const filteredFuncs = funcs
    .filter((func) => func.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .filter((func) => !showOnlyFavourites || favourites.some((fav) => fav.id === func.id))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="App">
      <div className="hidden modalBackdrop"></div>
      <div className="functions-wrapper">
        <Sidebar>
          <div className="toolbar">
            <button onClick={() => setPopupActive(true)}>Add Snippet</button>
            <button onClick={() => setShowOnlyFavourites((prev) => !prev)}>
              {showOnlyFavourites ? "Unfilter" : "Filter by Favourites"}
            </button>
          </div>
          <input
            type="text"
            placeholder="Search snippets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {filteredFuncs.map((func) => (
            <div
              key={func.id}
              className={`snippet-preview ${selectedFunc?.id === func.id ? "active" : ""}`}
              onClick={() => setSelectedFunc(func)}
            >
              {favourites.some((fav) => fav.id === func.id) && "⭐️"} {func.name.toLowerCase()}
            </div>
          ))}
        </Sidebar>

        <DetailsPanel
          data={selectedFunc}
          type="code snippet"
          onRemove={removeFunc}
          onCopy={handleCopyCode}
          onToggleFavourite={toggleFavourite}
          isFavourite={favourites.some((f) => f.id === selectedFunc?.id)}
        />
      </div>

      {popupActive && (
        <PopupForm
          title="Add a Code Snippet"
          form={form}
          onChange={(name, value) => setForm((prev) => ({ ...prev, [name]: value }))}
          onSubmit={handleSubmit}
          onClose={() => setPopupActive(false)}
          fields={FUNC_FIELDS}
        />
      )}
    </div>
  );
}

export default CodeLibrary;
